import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useT } from "@/i18n/I18nContext";
import { Link2, AlertCircle, User, Calendar } from "lucide-react";

/**
 * Live preview of which rental/customer will be linked when we save
 * a toll/ticket for a given car at a given datetime.
 */
export default function LinkPreview({ carId, datetimeIso }) {
  const { t } = useT();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!carId || !datetimeIso) {
      setData(null);
      return;
    }
    let cancelled = false;
    setLoading(true);
    const id = setTimeout(async () => {
      try {
        const r = await api.get("/link-preview", {
          params: { car_id: carId, datetime_iso: new Date(datetimeIso).toISOString() },
        });
        if (!cancelled) setData(r.data);
      } catch (e) {
        if (!cancelled) setData(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }, 300);
    return () => {
      cancelled = true;
      clearTimeout(id);
    };
  }, [carId, datetimeIso]);

  if (!carId || !datetimeIso) return null;

  if (loading) {
    return (
      <div className="col-span-2 text-[11px] font-mono text-zinc-400 italic px-3 py-2">
        {t("linkPreview.checking")}
      </div>
    );
  }

  if (!data) return null;

  if (!data.matched) {
    return (
      <div
        className="col-span-2 flex items-start gap-2 bg-zinc-50 border border-zinc-300 px-3 py-2 text-xs"
        data-testid="link-preview-none"
      >
        <AlertCircle className="w-4 h-4 text-zinc-400 mt-0.5 shrink-0" />
        <div>
          <div className="font-semibold text-zinc-700">{t("linkPreview.noMatch")}</div>
          <div className="text-zinc-500 mt-0.5">{t("linkPreview.noMatchHint")}</div>
        </div>
      </div>
    );
  }

  const { customer, rental } = data;
  return (
    <div
      className="col-span-2 bg-green-50 border border-green-300 px-3 py-2 text-xs space-y-1"
      data-testid="link-preview-matched"
    >
      <div className="flex items-center gap-1.5 text-green-800 font-semibold">
        <Link2 className="w-3.5 h-3.5" /> {t("linkPreview.willLink")}
      </div>
      {customer && (
        <div className="flex items-center gap-1.5 text-zinc-700 pl-5">
          <User className="w-3 h-3" />
          <span className="font-semibold">{customer.name}</span>
          {customer.phone && <span className="font-mono text-zinc-500">· {customer.phone}</span>}
        </div>
      )}
      {rental?.return_date && (
        <div className="flex items-center gap-1.5 text-zinc-600 pl-5 font-mono">
          <Calendar className="w-3 h-3" />
          {rental.pickup_date?.slice(0, 10)} → {rental.return_date.slice(0, 10)}
          <span className="text-zinc-400">· #{rental.id.slice(0, 8)}</span>
        </div>
      )}
    </div>
  );
}
